import { useEffect, useRef, useState } from "react";
import type { PrivacyHuntConfig, PrivacyLevel, PrivacyRoomItem, PrivacyVerdict } from "../scenario/types";
import type { PrivacyItemPin } from "../three/PrivacyRoomProps";
import { audio } from "../lib/audio";
import { t } from "../lib/uiStrings";
import { tx } from "../lib/tx";

const CLASS_LABEL: Record<PrivacyLevel, string> = {
  public: "Public",
  internal: "Internal",
  confidential: "Confidential",
  restricted: "Restricted",
};

const VERDICT_TONE: Record<PrivacyVerdict, "ok" | "protect"> = {
  display: "ok",
  secure: "protect",
  screen: "protect",
  check: "protect",
};

/**
 * Office-privacy discovery. Clicking an object opens one learning card.
 * It is not a scored safe/unsafe answer — the later sort activity does that.
 * The same component and the same nine items are used by office-under-attack
 * and data-handling.
 */
export function PrivacyHunt3D({
  config,
  items,
  pins,
  found,
  onFound,
  onEnded,
}: {
  config: PrivacyHuntConfig;
  items: PrivacyRoomItem[];
  pins: PrivacyItemPin[];
  found: Set<string>;
  onFound: (id: string) => void;
  onEnded: (foundIds: string[]) => void;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [showIntro, setShowIntro] = useState(true);
  const [listOpen, setListOpen] = useState(false);
  const spoke = useRef(false);
  const keepRef = useRef<HTMLButtonElement>(null);
  const total = items.length;
  const explored = items.filter((item) => found.has(item.id)).length;
  const allExplored = total > 0 && explored >= total;

  useEffect(() => {
    if (spoke.current) return;
    spoke.current = true;
    void config.timerSeconds;
    void audio.speak(t("priv.speak"), { voice: "onyx" });
    return () => audio.cancelSpeech();
  }, [config.timerSeconds]);

  useEffect(() => {
    if (!openId) return;
    keepRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpenId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openId]);

  const inspect = (item: PrivacyRoomItem) => {
    setShowIntro(false);
    setListOpen(false);
    setOpenId(item.id);
    if (!found.has(item.id)) onFound(item.id);
    audio.click();
  };

  const pinById = new Map(pins.map((pin) => [pin.id, pin]));
  const open = items.find((item) => item.id === openId) ?? null;
  const progress = t("priv.progress")
    .replace("{explored}", String(explored))
    .replace("{total}", String(total));

  return (
    <div className="privhunt privhunt-3d" aria-label={t("priv.title")}>
      <div className="privhunt-pins">
        {items.map((item) => {
          const pin = pinById.get(item.id);
          if (!pin || !pin.visible) return null;
          const seen = found.has(item.id);
          return (
            <button
              key={item.id}
              type="button"
              className={`hotspot-pin privhunt-pin ${seen ? "on" : ""}`}
              style={{ left: `${pin.xPct}%`, top: `${pin.yPct}%` }}
              onClick={() => inspect(item)}
              aria-label={item.card?.title ?? item.label}
            >
              <span className="hotspot-dot" aria-hidden />
              {seen && <span className="privhunt-seen" aria-hidden>✓</span>}
              <span className="hotspot-label">{item.card?.title ?? item.label}</span>
            </button>
          );
        })}
      </div>

      <section
        className={`privlearn-intro ${showIntro ? "" : "is-compact"}`}
        aria-label={t("priv.title")}
        onPointerDown={(event) => event.stopPropagation()}
      >
        {showIntro && (
          <div className="privlearn-copy">
            <div className="privlearn-kicker">{t("priv.kicker")}</div>
            <h1>{t("priv.title")}</h1>
            <p>{t("priv.description")}</p>
            <div className="privlearn-goal">
              <span aria-hidden>{tx("i")}</span>
              {t("priv.goal")}
            </div>
          </div>
        )}
        <div className="privlearn-side">
          <div className="privlearn-count">
            {progress.split(/(\d+)/).map((part, index) =>
              /^\d+$/.test(part) ? <strong key={index}>{part}</strong> : <span key={index}>{part}</span>,
            )}
          </div>
          <div className="privlearn-bar" aria-hidden>
            <div style={{ width: `${total ? Math.min(100, (explored / total) * 100) : 0}%` }} />
          </div>
          {allExplored && <p className="privlearn-done">{t("priv.done").replace("{total}", String(total))}</p>}
          {!showIntro && !allExplored && <p className="privlearn-hint">{t("priv.hint")}</p>}
          <div className="privlearn-actions">
            {!showIntro && (
              <button type="button" className="privlearn-textbtn" onClick={() => { setShowIntro(true); setOpenId(null); }}>
                {t("priv.instructions")}
              </button>
            )}
            <button type="button" className="privlearn-textbtn" onClick={() => setListOpen(true)}>
              {t("priv.list")}
            </button>
          </div>
          <button type="button" className="btn privlearn-continue" onClick={() => onEnded([...found])}>
            {t("priv.continue")}
          </button>
        </div>
      </section>

      {listOpen && (
        <div className="privlearn-list" role="dialog" aria-label={t("priv.list")} onPointerDown={(event) => event.stopPropagation()}>
          <button type="button" className="privlearn-x" aria-label={t("priv.close")} onClick={() => setListOpen(false)}>×</button>
          <h2>{t("priv.list")}</h2>
          <ul>
            {items.map((item) => (
              <li key={item.id}>
                <button type="button" onClick={() => inspect(item)}>
                  <span>{item.card?.title ?? item.label}</span>
                  {found.has(item.id) && <em>{t("priv.explored")}</em>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {open && (
        <article
          className="privhunt-card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="privhunt-card-title"
          onPointerDown={(event) => event.stopPropagation()}
        >
          <button type="button" className="privlearn-x" aria-label={t("priv.close")} onClick={() => setOpenId(null)}>×</button>
          <header className="privhunt-card-head">
            <div className="privhunt-thumb" aria-hidden>{open.icon}</div>
            <div>
              <h2 id="privhunt-card-title">{open.card?.title ?? open.label}</h2>
              <p className="privhunt-type">
                {t("priv.typeLabel")}: {open.card?.informationType ?? CLASS_LABEL[open.level]}
              </p>
              <p className="privhunt-class">
                {t("priv.classLabel")}: {CLASS_LABEL[open.level]}
              </p>
            </div>
          </header>
          <div className={`privhunt-banner is-${open.card ? VERDICT_TONE[open.card.verdict] : "protect"}`}>
            <span aria-hidden>{open.card?.verdict === "display" ? "✓" : "!"}</span>
            {open.card?.verdictLabel ?? t("priv.protect")}
          </div>
          <div className="privhunt-card-body">
            <section>
              <h3>{t("priv.found")}</h3>
              <p>{open.card?.found ?? open.reveal}</p>
            </section>
            <section>
              <h3>{t("priv.matters")}</h3>
              <p>{open.card?.matters ?? open.explain}</p>
            </section>
            <section>
              <h3>{t("priv.action")}</h3>
              <p>{open.card?.action ?? open.explain}</p>
            </section>
          </div>
          <button ref={keepRef} type="button" className="btn privhunt-keep" onClick={() => setOpenId(null)}>
            {t("priv.keep")} <span aria-hidden>→</span>
          </button>
        </article>
      )}
    </div>
  );
}
